import { useSelector, useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { ThemeProvider } from "@mui/material/styles";

import { RootState } from "@/store/store";
import {
    updateNewAppointment,
    updateExistAppointment,
} from "@/store/appointments/appointmentsSlice";
import cuttingTimeSlotsArr from "@/utils/cuttingTimeSlotsArr";
import { startTimeSlots, endTimeSlots, themeSelector } from "@/utils/constants";
import { AppointmentsMainRightPartProps } from "@/shared/interfaces/appointmentsPage";
import calculateDuration from "@/utils/calculateDoration";
import { normalizeTime } from "@/utils/normalizeTime";
import useGetRole from "@/hooks/useGetRole";

import "./AppointmentsMainTimeSelector.scss";

export default function AppointmentsMainTimeSelector({
    flag,
}: AppointmentsMainRightPartProps) {
    const dispatch = useDispatch();
    const { isAdmin } = useGetRole();

    const isEditAppointment = useSelector(
        (state: RootState) => state.ui.isEditAppointment,
    );
    const currentAppointment = useSelector((state: RootState) =>
        isEditAppointment
            ? state.appointments.existtAppointment
            : state.appointments.newAppointment,
    );
    const isAppointmentComplete = useSelector(
        (state: RootState) =>
            state.appointments.existtAppointment.isAppointmentComplete,
    );

    const { startTime, endTime } = currentAppointment;

    const index = startTimeSlots.findIndex(
        item => item.value === startTime.trim(),
    );
    const errors = useSelector((state: RootState) => state.ui.errors);

    const handleChange = (event: SelectChangeEvent) => {
        const newTime =
            typeof event?.target?.value === "string"
                ? event?.target?.value
                : "09:00";

        const updatedStartTime = flag === "start" ? newTime : startTime;
        const updatedEndTime =
            flag === "start"
                ? normalizeTime(endTime || "")
                : normalizeTime(newTime);

        const durationNum =
            updatedStartTime && updatedEndTime
                ? calculateDuration(updatedStartTime, updatedEndTime)
                : 0;

        const updatedData = {
            startTime: updatedStartTime,
            endTime: updatedEndTime,
            duration: durationNum,
        };

        if (isEditAppointment) {
            dispatch(updateExistAppointment(updatedData));
        } else {
            dispatch(updateNewAppointment(updatedData));
        }
    };

    const currentSlots =
        flag === "start"
            ? startTimeSlots
            : cuttingTimeSlotsArr(startTime).filter(item =>
                  endTimeSlots.find(end => end.value === item.value),
              );

    return (
        <ThemeProvider theme={themeSelector}>
            <Box sx={{ minWidth: 120 }}>
                <FormControl fullWidth>
                    <InputLabel
                        id='demo-simple-select-label'
                        sx={{
                            color: "#47586E",
                        }}
                    >
                        {flag === "start" ? "Час початку" : "Час закінчення"}
                    </InputLabel>
                    <Select
                        sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor:
                                    flag === "end" && errors.endTime?.message
                                        ? "#AD1313"
                                        : "#BCC3CD",
                                border:
                                    flag === "end" && errors.endTime?.message
                                        ? "2px solid #AD1313"
                                        : "1px solid #BCC3CD !important",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#BCC3CD",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#1367B5",
                            },
                        }}
                        labelId='demo-simple-select-label'
                        id='demo-simple-select'
                        error={
                            flag === "start"
                                ? !!errors.startTime &&
                                  errors.startTime.message.length > 0
                                : !!errors.endTime &&
                                  errors.endTime.message.length > 0
                        }
                        value={
                            flag === "start"
                                ? index !== -1
                                    ? startTimeSlots[index].value
                                    : ""
                                : currentSlots.find(
                                      slot =>
                                          slot.value ===
                                          normalizeTime(endTime || ""),
                                  )?.value || ""
                        }
                        label={
                            flag === "start" ? "Час початку" : "Час закінчення"
                        }
                        onChange={handleChange}
                        className='input_time_selector'
                        MenuProps={{
                            disableScrollLock: true,
                            PaperProps: {
                                sx: {
                                    maxHeight: 200,
                                    overflowY: "auto",
                                    marginTop: 1,
                                    padding: 0,
                                    "& ul": {
                                        padding: 0,
                                        margin: 0,
                                    },
                                },
                            },
                        }}
                        disabled={isAppointmentComplete || !isAdmin}
                    >
                        {currentSlots.map(item => (
                            <MenuItem
                                key={item.value}
                                value={item.value}
                                selected={
                                    flag === "start" &&
                                    item.value === startTimeSlots[index]?.value
                                }
                            >
                                {item.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </ThemeProvider>
    );
}
